/**
 * Warnings raised by the ASPICE fast-check gate when a force-approve was
 * blocked.
 *
 * The gate returns HTTP 200 with `confirmed: false` and leaves the thread
 * alone, so without this dialog the button appears to do nothing at all. The
 * user either goes back and fixes the requirement, or approves anyway — which
 * re-sends the request with `confirm: true`.
 */

import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  makeStyles,
} from '@material-ui/core';
import type { ApprovalWarning } from './api';

const useStyles = makeStyles(theme => ({
  intro: {
    marginBottom: theme.spacing(2),
  },
  high: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
  medium: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
  },
  low: {
    backgroundColor: theme.palette.info.main,
    color: theme.palette.info.contrastText,
  },
  approveAnyway: {
    color: theme.palette.error.main,
  },
}));

export interface ApprovalWarningsDialogProps {
  open: boolean;
  warnings: ApprovalWarning[];
  busy?: boolean;
  onCancel: () => void;
  /** Re-sends the force-approve with confirm=true. */
  onApproveAnyway: () => void;
}

export const ApprovalWarningsDialog: FC<ApprovalWarningsDialogProps> = ({
  open,
  warnings,
  busy = false,
  onCancel,
  onApproveAnyway,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('requirement-lifecycle');
  // Only shown when the caller attributed the warnings. One thread at a time
  // needs no such column, and adding an empty one would just be noise.
  const showRequirement = warnings.some(w => w.requirementId !== undefined);

  const severityClass = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'high':
        return classes.high;
      case 'medium':
        return classes.medium;
      default:
        return classes.low;
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>{t('approvalGate.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText className={classes.intro}>
          {t('approvalGate.description', { count: warnings.length })}
        </DialogContentText>
        <Table size="small">
          <TableHead>
            <TableRow>
              {showRequirement && (
                <TableCell>{t('approvalGate.columns.requirement')}</TableCell>
              )}
              <TableCell>{t('approvalGate.columns.rule')}</TableCell>
              <TableCell>{t('approvalGate.columns.severity')}</TableCell>
              <TableCell>{t('approvalGate.columns.violation')}</TableCell>
              <TableCell>{t('approvalGate.columns.fix')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {warnings.map((warning, index) => (
              <TableRow
                key={`${warning.requirementId ?? ''}-${
                  warning.ruleId
                }-${index}`}
              >
                {showRequirement && (
                  <TableCell>{warning.requirementId ?? '—'}</TableCell>
                )}
                <TableCell>{warning.ruleId}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={warning.severity}
                    className={severityClass(warning.severity)}
                  />
                </TableCell>
                <TableCell>{warning.violation}</TableCell>
                <TableCell>{warning.fix}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          {t('approvalGate.actions.cancel')}
        </Button>
        <Button
          className={classes.approveAnyway}
          disabled={busy}
          onClick={onApproveAnyway}
        >
          {t('approvalGate.actions.approveAnyway')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApprovalWarningsDialog;
